const ROLES = {
  OWNER: "Owner",
  FAMILY_USER: "Family User",
  CAREGIVER: "Caregiver",
  DOCTOR: "Doctor",
};

// List of all feature keys, matching the routes and sidebar links
const FEATURES = {
  DASHBOARD: "dashboard",
  VITALS: "vitals",
  DAILY_CARE_LOG: "daily-care-log",
  ACTIVE_PRN_MEDS: "active-prn-meds",
  MEDICATION_ADMINISTRATION: "medication-administration",
  MEDICATION_INVENTORY: "medication-inventory",
  PRESCRIPTION: "prescription",
  INCIDENT_REPORTING: "incident-reporting",
  LAB_REPORTS: "lab-reports",
  APPOINTMENTS: "appointments",
  NOTES_OBSERVATIONS: "notes-observations",
  CARE_TEAM: "care-team",
  MANAGE_PROFILES: "manage-profiles",
};

/**
 * Defines the permissions for each role.
 * 'read' allows viewing the feature's data.
 * 'write' allows creating, editing, or deleting the feature's data.
 * 'manage' allows performing administrative actions for a feature, like changing its settings.
 */
const permissions = {
  [FEATURES.VITALS]: {
    [ROLES.OWNER]: ["read", "write", "delete", "manage"],
    [ROLES.FAMILY_USER]: ["read", "write", "delete", "manage"],
    [ROLES.CAREGIVER]: ["read", "write"],
    [ROLES.DOCTOR]: ["read"],
  },
  [FEATURES.DAILY_CARE_LOG]: {
    [ROLES.OWNER]: ["read", "write", "delete", "manage"],
    [ROLES.FAMILY_USER]: ["read", "write", "delete"],
    [ROLES.CAREGIVER]: ["read", "write"],
    [ROLES.DOCTOR]: ["read"],
  },
  [FEATURES.ACTIVE_PRN_MEDS]: {
    [ROLES.OWNER]: ["read", "write", "delete", "manage"],
    [ROLES.FAMILY_USER]: ["read", "write"],
    [ROLES.CAREGIVER]: ["read"],
    [ROLES.DOCTOR]: ["read"],
  },
  [FEATURES.MEDICATION_ADMINISTRATION]: {
    [ROLES.OWNER]: ["read", "write", "delete", "manage"],
    [ROLES.FAMILY_USER]: ["read", "write"],
    [ROLES.CAREGIVER]: ["read", "write"],
    [ROLES.DOCTOR]: ["read"],
  },
  [FEATURES.MEDICATION_INVENTORY]: {
    [ROLES.OWNER]: ["read", "write", "delete", "manage"],
    [ROLES.FAMILY_USER]: ["read", "write"],
    [ROLES.CAREGIVER]: ["read", "write"],
    [ROLES.DOCTOR]: ["read"],
  },
  [FEATURES.PRESCRIPTION]: {
    [ROLES.OWNER]: ["read", "write", "share", "delete", "manage"],
    [ROLES.FAMILY_USER]: ["read", "write", "share", "delete"],
    [ROLES.CAREGIVER]: ["read", "write", "share"],
    [ROLES.DOCTOR]: ["read", "write", "share"],
  },
  [FEATURES.INCIDENT_REPORTING]: {
    [ROLES.OWNER]: ["read", "write", "delete", "manage"],
    [ROLES.FAMILY_USER]: ["read", "write", "delete"],
    [ROLES.CAREGIVER]: ["read", "write"],
    [ROLES.DOCTOR]: ["read"],
  },
  [FEATURES.LAB_REPORTS]: {
    [ROLES.OWNER]: ["read", "write", "share", "delete", "manage"],
    [ROLES.FAMILY_USER]: ["read", "write", "share", "delete"],
    [ROLES.CAREGIVER]: ["read", "share"],
    [ROLES.DOCTOR]: ["read", "share"],
  },
  [FEATURES.APPOINTMENTS]: {
    [ROLES.OWNER]: ["read", "write", "delete", "manage"],
    [ROLES.FAMILY_USER]: ["read", "write", "delete"],
    [ROLES.CAREGIVER]: ["read", "write", "delete"],
    [ROLES.DOCTOR]: [],
  },
  [FEATURES.NOTES_OBSERVATIONS]: {
    [ROLES.OWNER]: ["read", "write", "delete", "manage"],
    [ROLES.FAMILY_USER]: ["read", "write", "delete"],
    [ROLES.CAREGIVER]: ["read", "write"],
    [ROLES.DOCTOR]: ["read"],
  },
  [FEATURES.CARE_TEAM]: {
    [ROLES.OWNER]: ["read", "write", "delete", "manage"],
    [ROLES.FAMILY_USER]: [],
    [ROLES.CAREGIVER]: [],
    [ROLES.DOCTOR]: [],
  },
};

/**
 * A helper function to check if a user with a given role has a specific permission for a feature.
 * @param {string} role - The role of the user (e.g., ROLES.OWNER).
 * @param {string} feature - The feature to check (e.g., FEATURES.VITALS).
 * @param {string} permission - The permission to check.
 * @returns {boolean} - True if the user has the permission, false otherwise.
 */
const hasPermission = (role, feature, permission) => {
  return permissions[feature]?.[role]?.includes(permission) ?? false;
};

export { ROLES, FEATURES, hasPermission, permissions };
